import React, { FunctionComponent, useEffect, useState } from 'react'
import Moment from 'react-moment'
import { Navigate, Route, Routes, useParams, useLocation } from 'react-router'
import axios from 'axios'
import { AccountDataUtil, AccountDetails, HostData, HostDataUtil, MaintenanceAndSupportStatus } from '../Customer/Data'
import { HostState } from '../../redux/hosts'
import { defaultAuthHandler } from '../../auth'
import {
  CalendarClockIcon,
  EarthIcon,
  InfoIcon,
  LicenseManagerIcon,
  OfficeBuildingIcon,
  OfficePrinterIcon,
  PaperCutOutlineIcon,
} from '../Icons/Icons'
import { BriefsHub } from './BriefsHub'
import { useLatestProductOrders } from './LatestProductOrders'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import RouteTabLink from '../Tab/RouteTabLink'
import styles from './CustomerDetail.module.css'
import Toaster from '../Toaster'

/**
 * Returns the earlier of the maintenance-and-support expiry and the license expiry, or undefined
 * when neither is set. Used to render the "Nearest expiry" row on the customer detail panel.
 */
export function nearestExpiry(account?: AccountDetails): Date | undefined {
  if (!account) return undefined
  const ms = AccountDataUtil.toDate(account.maintenanceAndSupportExpiryDate)
  const lic = AccountDataUtil.toDate(account.licenseExpiryDate)
  if (!ms && !lic) return undefined
  if (!ms) return lic
  if (!lic) return ms
  return ms < lic ? ms : lic
}

/**
 * Returns the label for the "License" row: "M&S" when an active maintenance-and-support
 * contract is present, otherwise the plain license type. Empty string when no useful data exists.
 */
export function licenseLabel(account?: AccountDetails): string {
  if (!account) return ''
  const status = account.maintenanceAndSupportStatus
  if (status === MaintenanceAndSupportStatus.Current || status === MaintenanceAndSupportStatus.Expiring) {
    return 'M&S'
  }
  return account.licenseType || ''
}

interface DetailRowProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}

const DetailRow: FunctionComponent<DetailRowProps> = ({ icon, label, value }) => (
  <div className='flex'>
    <div className='push flex-noshrink'>
      <i className='icon thirtytwo'>{icon}</i>
    </div>
    <div>
      <h5>{label}</h5>
      <p>{value}</p>
    </div>
  </div>
)

interface PanelProps {
  teamID: string
  host: HostData
  hostState?: HostState
}

const CustomerDetailsPanel: FunctionComponent<PanelProps> = ({ teamID, host, hostState }) => {
  const fleetSize = hostState?.systemInfo?.embeddedCount
  const accountExpiry = nearestExpiry(hostState?.accountDetails)
  const license = licenseLabel(hostState?.accountDetails)
  const { data: orders } = useLatestProductOrders(teamID, host.id)
  const bqExpiry = orders?.currentSupportExpiry ? new Date(orders.currentSupportExpiry) : undefined
  const expiry = accountExpiry ?? bqExpiry

  return (
    <div className='surface fh'>
      <header className={styles.surfaceHeader}>
        <h3>Summary</h3>
      </header>
      {fleetSize !== undefined && (
        <DetailRow
          icon={<OfficePrinterIcon />}
          label='Size of fleet'
          value={
            <>
              {fleetSize} <small>devices</small>
            </>
          }
        />
      )}
      <DetailRow
        icon={<EarthIcon />}
        label='Environment'
        value={HostDataUtil.getInfrastructureShortName(host.infrastructure)}
      />
      <DetailRow icon={<PaperCutOutlineIcon />} label='Product' value={HostDataUtil.getProductText(host.product)} />
      {expiry && (
        <DetailRow
          icon={<CalendarClockIcon />}
          label='Nearest expiry'
          value={<Moment format='D MMM YYYY' date={expiry} />}
        />
      )}
      <DetailRow icon={<OfficeBuildingIcon />} label='Industry' value={host.industry || '—'} />
      {license && <DetailRow icon={<LicenseManagerIcon />} label='License' value={license} />}
    </div>
  )
}

interface CustomerProfile {
  customerId: string
  name: string
  industry: string
  location: string
  otherLocations: string[]
  bio: string
  notes: string
  companyValues: string[]
  numberOfEmployees: string
  currencyCode: string
  yearFounded: string
  website?: string
  deviceValue?: string | number
  pageCostBW?: string | number
  pageCostColor?: string | number
}

interface IntelTabProps {
  customerID: string
}

const IntelTab: FunctionComponent<IntelTabProps> = ({ customerID }) => {
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleFieldChange = (field: keyof CustomerProfile, value: any) => {
    if (profile) {
      setProfile({ ...profile, [field]: value })
    }
  }

  const handleSave = () => {
    if (!profile) return
    setSaving(true)

    const teamID = defaultAuthHandler.getTeamID()

    const payload = {
      ...profile,
      yearFounded: profile.yearFounded ? parseInt(String(profile.yearFounded), 10) || 0 : 0,
      deviceValue: profile.deviceValue ? parseFloat(String(profile.deviceValue)) || 0 : 0,
      pageCostBW: profile.pageCostBW ? parseFloat(String(profile.pageCostBW)) || 0 : 0,
      pageCostColor: profile.pageCostColor ? parseFloat(String(profile.pageCostColor)) || 0 : 0,
    }

    axios
      .put<CustomerProfile>(`/api/teams/${teamID}/customers/${customerID}/profile`, payload)
      .then(response => {
        setProfile(response.data)
        Toaster.notifySuccess('Customer profile saved.')
        setSaving(false)
      })
      .catch(err => {
        Toaster.notifyFailure(err.message || 'An error occurred while saving the customer profile')
        setSaving(false)
      })
  }

  useEffect(() => {
    const controller = new AbortController()
    const teamID = defaultAuthHandler.getTeamID()

    setLoading(true)
    setError(null)

    axios
      .get<CustomerProfile>(`/api/teams/${teamID}/customers/${customerID}/profile`, {
        signal: controller.signal,
      })
      .then(response => {
        setProfile(response.data)
        setLoading(false)
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          setError(err.message || 'An error occurred while fetching the customer profile')
          setLoading(false)
        }
      })

    return () => {
      controller.abort()
    }
  }, [customerID])

  if (loading) {
    return (
      <div className={`empty loading ${styles.loadingContainer}`} data-testid='loading-dots'>
        <div className={styles.loadingDotsWrapper}>
          <LoadingDots />
        </div>
        <div className={styles.loadingText}>
          <h4 className={styles.loadingTextTitle}>Building your insights...</h4>
          Next time will be faster 🚀
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.alertFlex}`} data-testid='profile-error'>
        <div>Failed to load customer profile: {error}</div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className={`alert error ${styles.alertFlex}`} data-testid='profile-empty'>
        <div>No customer profile found.</div>
      </div>
    )
  }

  return (
    <div data-testid='intel-tab'>
      {/* Organisation profile surface */}
      <div className={`surface ${styles.surfaceMb}`}>
        <header className={styles.surfaceHeader}>
          <h3>Organization profile</h3>
        </header>

        <div className={`alert info dip flex-ver ${styles.surfaceMb}`}>
          <i className='icon twelve'>
            <InfoIcon />
          </i>
          <div>
            <strong>Profile and context is used to inform the AI insights</strong>
            <br />
            The more information you provide here, the better. Try to focus on information that is not readily available
            on the web, including company values, internal policies, key stakeholder names. Do not put private
            information such as phone numbers.
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-name'>Name</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-name'
              type='text'
              value={profile.name || ''}
              onChange={e => handleFieldChange('name', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-website'>Website</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-website'
              type='text'
              value={profile.website || ''}
              onChange={e => handleFieldChange('website', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-industry'>Industry</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-industry'
              type='text'
              value={profile.industry || ''}
              onChange={e => handleFieldChange('industry', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-location'>Location</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-location'
              type='text'
              value={profile.location || ''}
              onChange={e => handleFieldChange('location', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-other-locations'>Other locations</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-other-locations'
              type='text'
              value={profile.otherLocations ? profile.otherLocations.join(', ') : ''}
              onChange={e =>
                handleFieldChange(
                  'otherLocations',
                  e.target.value.split(',').map(s => s.trim())
                )
              }
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-year-founded'>Year founded</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-year-founded'
              type='text'
              value={profile.yearFounded || ''}
              onChange={e => handleFieldChange('yearFounded', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-employees'>Number of employees</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-employees'
              type='text'
              value={profile.numberOfEmployees || ''}
              onChange={e => handleFieldChange('numberOfEmployees', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-company-values'>Company values</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-company-values'
              type='text'
              value={profile.companyValues ? profile.companyValues.join(', ') : ''}
              onChange={e =>
                handleFieldChange(
                  'companyValues',
                  e.target.value.split(',').map(s => s.trim())
                )
              }
            />
          </div>
        </div>
      </div>

      {/* AI customization surface */}
      <div className={`surface ${styles.surfaceMb}`}>
        <header className={styles.surfaceHeader}>
          <h3>AI customization</h3>
          <p className={styles.headerSubtext}>
            This content is used to position and create bespoke and customized summaries relevant to this customer.
          </p>
        </header>

        <div className='field'>
          <label htmlFor='intel-bio'>
            Organization bio{' '}
            <i className={`icon twelve ${styles.labelInfoIcon}`}>
              <InfoIcon />
            </i>
          </label>
          <p className={styles.fieldHelpText}>
            Used to customize Briefs. Initially auto-generated, you can edit or add any details you want to further
            customize output.
          </p>
          <textarea
            id='intel-bio'
            value={profile.bio || ''}
            onChange={e => handleFieldChange('bio', e.target.value)}
            rows={4}
            className={styles.textareaFull}
          />
        </div>

        <div className='field'>
          <label htmlFor='intel-notes'>
            Notes{' '}
            <i className={`icon twelve ${styles.labelInfoIcon}`}>
              <InfoIcon />
            </i>
          </label>
          <p className={styles.fieldHelpText}>
            Consider brain-dumping everything you know about this customer. The more context, the more likely the system
            is to suggest relevant information. eg. the customer prides itself on community engagement and ESG.
          </p>
          <textarea
            id='intel-notes'
            value={profile.notes || ''}
            onChange={e => handleFieldChange('notes', e.target.value)}
            rows={4}
            className={styles.textareaFull}
          />
        </div>
      </div>

      {/* Value estimation surface */}
      <div className={`surface ${styles.surfaceMb}`}>
        <header className={styles.surfaceHeader}>
          <h3>Value estimation</h3>
          <p className={styles.headerSubtext}>Used for estimating contract value per annum.</p>
        </header>

        <div className='field'>
          <label htmlFor='intel-currency'>Currency</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-currency'
              type='text'
              value={profile.currencyCode || ''}
              onChange={e => handleFieldChange('currencyCode', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-value-per-device'>Value per device</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-value-per-device'
              type='text'
              value={profile.deviceValue ?? ''}
              onChange={e => handleFieldChange('deviceValue', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-cost-per-bw-page'>Cost per B&W page printed</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-cost-per-bw-page'
              type='text'
              value={profile.pageCostBW ?? ''}
              onChange={e => handleFieldChange('pageCostBW', e.target.value)}
            />
          </div>
        </div>

        <div className='field'>
          <label htmlFor='intel-cost-per-color-page'>Cost per Color page printed</label>
          <div className='flex flex-nowrap'>
            <input
              id='intel-cost-per-color-page'
              type='text'
              value={profile.pageCostColor ?? ''}
              onChange={e => handleFieldChange('pageCostColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={styles.saveActions}>
        <button
          className={`btn ${saving ? 'loading' : ''} ${styles.btnMinWidth}`}
          onClick={handleSave}
          disabled={saving}
        >
          <LoadingDots />
          <span className='text'>Update</span>
        </button>
      </div>
    </div>
  )
}

interface Props {
  teamID: string
  hosts: HostData[]
  hostStates: HostState[]
}

// Matches any single-level brief detail path. Until we migrate to React Router
// layout routes, this is how the tabbed shell knows to step aside.
function isBriefDetailPath(pathname: string): boolean {
  return /\/briefs\/[^/]+$/.test(pathname)
}

export const CustomerDetail: FunctionComponent<Props> = props => {
  const { customerID } = useParams<{ customerID: string }>()
  const location = useLocation()
  const host = props.hosts.find(h => h.id === customerID)

  if (!host) {
    return <Navigate to='/intelligence-hub' replace />
  }

  const hostState = props.hostStates.find(h => h.id === customerID)

  // routing is different if we're showing a brief
  // TODO promote to proper routing when the pattern is better established.
  if (isBriefDetailPath(location.pathname)) {
    return (
      <div className={`auto ${styles.briefPadding}`}>
        <Routes>
          <Route path='briefs/*' element={<BriefsHub teamID={props.teamID} host={host} />} />
        </Routes>
      </div>
    )
  }

  const overviewPath = `/intelligence-hub/${host.id}`

  return (
    <div className='tab' id='tab-intelligence-hub-customer'>
      <header>
        <div className='auto'>
          <div className='flex flex-ver'>
            <div className='flex-grow flex flex-ver'>
              <h1 className='push'>{host.name}</h1>
            </div>
          </div>
        </div>
        <div className='auto'>
          <div className='tabs'>
            <ul className='auto'>
              <RouteTabLink path={overviewPath}>Overview</RouteTabLink>
              <RouteTabLink path={`${overviewPath}/intel`}>Intel</RouteTabLink>
              <RouteTabLink path={`${overviewPath}/briefs`}>Briefs</RouteTabLink>
            </ul>
          </div>
        </div>
      </header>

      <div className='auto'>
        <div className={styles.splitRow}>
          <div className={styles.splitSidebar}>
            <CustomerDetailsPanel teamID={props.teamID} host={host} hostState={hostState} />
          </div>
          <div className={styles.splitContent}>
            <Routes>
              <Route index element={null} />
              <Route path='intel' element={<IntelTab customerID={host.id} />} />
              <Route path='briefs/*' element={<BriefsHub teamID={props.teamID} host={host} />} />
              <Route path='*' element={<Navigate to='.' replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  )
}
